import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useImpersonation } from '@/hooks/useImpersonation'

export default function ImpersonationBanner() {
  const navigate = useNavigate()
  const { isImpersonating, tenantName, tenantSlug, startedAt, exitImpersonation, isEnding } =
    useImpersonation()

  const handleExit = useCallback(async () => {
    await exitImpersonation()
    navigate('/employee/admin')
  }, [exitImpersonation, navigate])

  // Handle ESC key to exit impersonation
  useEffect(() => {
    if (!isImpersonating) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleExit()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isImpersonating, handleExit])

  if (!isImpersonating) return null

  const formatDuration = () => {
    if (!startedAt) return ''
    const start = new Date(startedAt)
    const now = new Date()
    const diffMs = now.getTime() - start.getTime()
    const minutes = Math.floor(diffMs / 60000)

    if (minutes < 1) return 'Just started'
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ${minutes % 60}m`
  }

  return (
    <>
      {/* Full-page overlay tint */}
      <div
        className="fixed inset-0 pointer-events-none z-30"
        style={{ backgroundColor: 'rgba(59, 130, 246, 0.06)' }}
      />

      {/* Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            {/* Left: Status */}
            <div className="flex items-center gap-3">
              {/* Pulsing dot */}
              <div className="relative">
                <div className="w-2.5 h-2.5 bg-white rounded-full" />
                <div className="absolute inset-0 w-2.5 h-2.5 bg-white rounded-full animate-ping opacity-75" />
              </div>

              <span className="font-semibold text-sm">Impersonating</span>

              {/* Tenant info */}
              <div className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg">
                <span className="font-mono text-sm">{tenantSlug || tenantName}</span>
                {tenantName && tenantSlug && (
                  <span className="text-blue-200 text-xs">({tenantName})</span>
                )}
              </div>

              {/* Duration */}
              <span className="text-blue-200 text-xs">{formatDuration()}</span>
            </div>

            {/* Right: Exit */}
            <div className="flex items-center gap-3">
              <span className="text-blue-200 text-xs hidden sm:inline">
                Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white font-mono">ESC</kbd> to exit
              </span>

              <button
                onClick={handleExit}
                disabled={isEnding}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isEnding ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                )}
                Exit
              </button>
            </div>
          </div>
        </div>

        {/* Animated bottom border */}
        <div className="h-0.5 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 animate-pulse" />
      </div>

      {/* Spacer to push content down */}
      <div className="h-12" />
    </>
  )
}
